import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
// Type only: the payload SHAPE belongs to the screen that builds it, and a type import
// is erased at compile time, so `core/` gains no runtime dependency on a feature.
import type { ProfileSectionPayload } from '../../features/my-profile/profile-payload';
import {
  asRecord,
  readBoolean,
  readNumber,
  readString,
  UnknownRecord,
} from '../../shared/parsing/unknown-record';

export const MY_PROFILE_URL = `${environment.apiUrl}/my/profile`;

/**
 * The signed-in customer's own profile — `GET /my/profile`, one request per session,
 * plus `PATCH /my/profile` for the profile editor (S-12): still ONE cache, because the
 * save replaces it with the response instead of invalidating it.
 *
 * Two consumers READ it: `establishmentOnlyGuard` (core/auth) and the
 * closed-course-request screen; the panel sidenav answers its menu from
 * `CustomerProfileService` (S-11) — two caches of the same response, the debt named
 * in S-10 impl-review F2. That is why this one lives in `core/` — a feature-local
 * service would be imported by `core/layout/`, upside down from the repo's
 * structure. The single-flight cache
 * (`shareReplay({ bufferSize: 1, refCount: false })`, the `pendingRefresh` pattern
 * from `CustomerSessionService`) is what turns the guard and the screen into ONE
 * network request instead of two parallel ones on every route entry.
 *
 * `reset()` exists for one concrete reason: nothing in this application reloads the
 * page when the account changes — logging out and logging in both end in a router
 * navigation — so the same application instance lives on. Without the reset, logging
 * into another account would read the PREVIOUS customer's `customerType` out of the
 * cache, and the Q-63/A access scope stands on that value. Hence BOTH ends of the
 * account's life are covered, and both from ONE owner: `CustomerSessionService`
 * calls this on every successful login (S-10 impl-review F1) and on every path that
 * drops the session (S-15 phase 3) — logout is not the only road to a second account
 * in one tab, and not every logout goes through the panel layout.
 *
 * What this service does NOT do: it never classifies HTTP failures. 401/403 belong
 * to the interceptor's silent refresh and to `customerAuthGuard`; everything else is
 * rethrown for the caller to treat as "profile unavailable" (fail-closed at the
 * guard, error state on the screen). A failed attempt clears the cache so the next
 * caller retries instead of replaying the error for the rest of the session.
 */
@Service()
export class MyProfileService {
  private readonly http = inject(HttpClient);

  private cache: Observable<MyProfile> | null = null;

  load(): Observable<MyProfile> {
    const cached = this.cache;

    if (cached !== null) {
      return cached;
    }

    const attempt = this.http.get<unknown>(MY_PROFILE_URL).pipe(
      map((body) => parseMyProfile(body)),
      catchError((error: unknown) => {
        // Not `finalize`: a success must keep the cache, only a failure may drop it.
        this.cache = null;

        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.cache = attempt;

    return attempt;
  }

  /**
   * One section of the profile, saved with `PATCH /my/profile` (S-12).
   *
   * The response carries the WHOLE profile in the same shape as the `GET`, so it goes
   * through the same parser — a second parser for one contract is how two readings of
   * one endpoint start to differ.
   *
   * On success the cache is REPLACED rather than cleared: the sidenav and
   * `establishmentOnlyGuard` read this service on every route entry, and dropping the
   * cache would buy a second network request for every single save.
   *
   * On failure the cache is dropped and the error is rethrown WITHOUT classification —
   * 401 belongs to the interceptor's silent refresh, 400/403/429 to the screen, which
   * is the only place that knows which section the customer was saving.
   *
   * No `withCredentials` and no `X-CSRF-Token` here: `api.interceptor.ts` adds both to
   * every method outside `SAFE_METHODS`, and `PATCH` is one of them. Repeating it in a
   * service would be a second implementation of one rule.
   */
  save(payload: ProfileSectionPayload): Observable<MyProfile> {
    return this.http.patch<unknown>(MY_PROFILE_URL, payload).pipe(
      map((body) => parseMyProfile(body)),
      tap((profile) => {
        this.cache = of(profile);
      }),
      catchError((error: unknown) => {
        this.cache = null;

        return throwError(() => error);
      }),
    );
  }

  /**
   * ONE caller — `CustomerSessionService`, after a successful login and from
   * `dropSession()`, i.e. on every path that ENDS a session (see the class comment).
   *
   * It had a second one between S-14 phase 3 and Q-211/A: the confirmation of a login
   * address change called this directly, because Q-163/A said such a change left the
   * session alive and only staled the cache. Q-183/A withdrew that — the backend now
   * stamps `credentialsChangedAt` inside the confirming transaction, so the session is
   * over — and the screen calls `logout()`, which reaches this method through
   * `dropSession()` like every other road out of a session. One owner again.
   */
  reset(): void {
    this.cache = null;
  }
}

/**
 * Narrowing of `GET /my/profile` (and of the `PATCH` response, which carries the
 * SAME shape), frozen against the presenter's allowlist key for key — a mismatch
 * is reported, never patched here.
 *
 * `name`/`placementType`/`stateId` live on the TOP level of the response, not
 * inside `addressData`. `contactData` carries SIX keys since S-12: the two stored
 * numbers `workPhone` and `privatePhone`, plus `phone`, which the presenter
 * DERIVES from them (`workPhone ?? privatePhone ?? legacy phone`). The derived key
 * is an addition, not a replacement — it is read for display and never sent back,
 * because writing a derived value would overwrite whichever of the two it came from.
 */
export interface MyProfileAddress {
  commune: string | null;
  city: string | null;
  postalCode: string | null;
  street: string | null;
  buildingNumber: string | null;
  localNumber: string | null;
}

export interface MyProfileContact {
  firstName: string | null;
  lastName: string | null;
  function: string | null;
  /** DERIVED by the presenter from the two below — read only, never sent back. */
  phone: string | null;
  workPhone: string | null;
  privatePhone: string | null;
}

export interface MyProfileInvoice {
  nip: string | null;
  name: string | null;
  city: string | null;
  postalCode: string | null;
  street: string | null;
  buildingNumber: string | null;
  localNumber: string | null;
}

export interface MyProfile {
  /** 0 = osoba prywatna, 1 = placówka; any other value counts as "no access" (Q-63/A). */
  customerType: number;
  /** The LOGIN address of the account — never an invoice or contact e-mail. */
  email: string | null;
  name: string | null;
  surname: string | null;
  phoneNumber: string | null;
  stateId: number | null;
  /** uuid from the establishment-types dictionary. */
  placementType: string | null;
  /**
   * The NAME of a customer function, not its uuid — the admin panel stores the
   * label in this column, so a uuid written here would render as a blank select
   * for the office (Q-91/A).
   */
  customerFunction: string | null;
  /** The column is `boolean DEFAULT false`; a non-boolean body reads as `false`. */
  marketingAgreement: boolean;
  addressData: MyProfileAddress;
  contactData: MyProfileContact;
  invoiceBuyerData: MyProfileInvoice;
  invoiceReceiverData: MyProfileInvoice;
}

/** A broken profile body is a broken contract — named, never a bare `TypeError`. */
export class MyProfileParseError extends Error {
  constructor(reason: string) {
    super(`Odpowiedź /my/profile ma nieoczekiwany kształt: ${reason}`);
    this.name = 'MyProfileParseError';
  }
}

/** A missing nested object reads the same as one full of nulls — nothing to prefill. */
function nested(record: UnknownRecord, key: string): UnknownRecord {
  return asRecord(record[key]) ?? {};
}

function parseAddress(source: UnknownRecord): MyProfileAddress {
  return {
    commune: readString(source, 'commune'),
    city: readString(source, 'city'),
    postalCode: readString(source, 'postalCode'),
    street: readString(source, 'street'),
    buildingNumber: readString(source, 'buildingNumber'),
    localNumber: readString(source, 'localNumber'),
  };
}

function parseContact(source: UnknownRecord): MyProfileContact {
  return {
    firstName: readString(source, 'firstName'),
    lastName: readString(source, 'lastName'),
    function: readString(source, 'function'),
    phone: readString(source, 'phone'),
    workPhone: readString(source, 'workPhone'),
    privatePhone: readString(source, 'privatePhone'),
  };
}

function parseInvoice(source: UnknownRecord): MyProfileInvoice {
  return {
    nip: readString(source, 'nip'),
    name: readString(source, 'name'),
    city: readString(source, 'city'),
    postalCode: readString(source, 'postalCode'),
    street: readString(source, 'street'),
    buildingNumber: readString(source, 'buildingNumber'),
    localNumber: readString(source, 'localNumber'),
  };
}

/**
 * Key by key, on every nesting level — a field added backend-side reaches nothing
 * until somebody decides it should. Old jsonb rows may carry junk (e.g. a textual
 * `stateId`); a field of the wrong shape becomes `null` instead of blowing up the
 * screen. `customerType` is the one exception: without it the access scope cannot
 * be decided, so its absence is a parse failure.
 */
export function parseMyProfile(body: unknown): MyProfile {
  const record = asRecord(body);

  if (record === null) {
    throw new MyProfileParseError('oczekiwano obiektu profilu');
  }

  const customerType = readNumber(record, 'customerType');

  if (customerType === null) {
    throw new MyProfileParseError('brak pola "customerType"');
  }

  return {
    customerType,
    email: readString(record, 'email'),
    name: readString(record, 'name'),
    surname: readString(record, 'surname'),
    phoneNumber: readString(record, 'phoneNumber'),
    stateId: readNumber(record, 'stateId'),
    placementType: readString(record, 'placementType'),
    customerFunction: readString(record, 'customerFunction'),
    // `readBoolean` is strict `=== true`: anything else (missing key, string
    // "false", legacy null) reads as `false`, because the contract says boolean and
    // a `null` here would force every consumer to handle a third state.
    marketingAgreement: readBoolean(record, 'marketingAgreement'),
    addressData: parseAddress(nested(record, 'addressData')),
    contactData: parseContact(nested(record, 'contactData')),
    invoiceBuyerData: parseInvoice(nested(record, 'invoiceBuyerData')),
    invoiceReceiverData: parseInvoice(nested(record, 'invoiceReceiverData')),
  };
}
